import axios, { AxiosInstance } from 'axios'

import { config } from '../configs/api.config'

import { configureClient } from './api-utils'

let client: AxiosInstance

export interface HubspotCompany {
  company_id: string
  company_name: string | null
  domain: string | null
}

export interface HubspotCompaniesResponse {
  results: HubspotCompany[]
  total: number
}

export interface HubspotOrganizationData {
  company_id: string
  company_name: string | null
  domain: string | null
  current_mrr: string | null
  potential_mrr: string | null
  company_size: string | null
  pricing_tier: string | null
  lifecyclestage: string | null
  industry: string | null
  numberofemployees: string | null
  customer_number: string | null
  no_of_users: string | null
  activation_rate: string | null
  active_features: string | null
  usecases_implemented: string | null
  buyer_journey: string | null
  country: string | null
  city: string | null
  properties: Record<string, unknown> | null
}

export interface HubspotCoverageItem {
  org_id: string
  name: string
  organization_use: 'Production' | 'Sandbox'
  parent_production_org_id?: string
  hubspot_company_id?: string
  inherited_parent_hubspot_company_id?: string
  exact_hubspot_match?: HubspotCompany & {
    organization_id: string | null
  }
}

export type HubspotConflictReason =
  | 'linked_company_claims_other_org'
  | 'other_company_claims_this_org'
  | 'multiple_companies_claim_this_org'
  | 'company_shared_across_org_families'

export interface HubspotCompanyRef extends HubspotCompany {
  organization_id: string | null
}

export interface HubspotConflictItem extends HubspotCoverageItem {
  reasons: HubspotConflictReason[]
  linked_company?: HubspotCompanyRef
  claiming_companies: HubspotCompanyRef[]
  shared_with_org_ids: string[]
}

export interface HubspotCoverageResponse {
  summary: {
    total: number
    linked: number
    unlinked: number
    production_total: number
    production_linked: number
    production_unlinked: number
    sandbox_total: number
    sandbox_linked: number
    sandbox_unlinked: number
    exact_unlinked_matches: number
    sandbox_can_inherit_from_parent: number
    conflicts: number
  }
  exact_unlinked_matches: HubspotCoverageItem[]
  sandbox_can_inherit_from_parent: HubspotCoverageItem[]
  conflicts: HubspotConflictItem[]
  unlinked: HubspotCoverageItem[]
}

export interface HubspotTenantSyncAction {
  org_id: string
  name: string
  organization_use: 'Production' | 'Sandbox'
  company_id: string
  company_id_source: 'organization' | 'parent_production_org'
  tenant_record_id?: string
  operations: (
    | 'create_tenant'
    | 'update_tenant'
    | 'associate_company'
    | 'remove_stale_association'
  )[]
  properties: Record<string, string>
  /** Companies the Tenant would be detached from. */
  stale_company_ids?: string[]
  skipped_reason?: 'no_hubspot_company'
}

export interface HubspotTenantSyncResult {
  dry_run: boolean
  object_type_id: string
  /** Properties omitted because they do not exist on the HubSpot Tenant object. */
  unavailable_properties: string[]
  summary: {
    considered: number
    created: number
    updated: number
    associated: number
    unchanged: number
    skipped_no_company: number
    associations_to_add: number
    stale_associations_to_remove: number
    /** Records with any work to do — gate Apply on this, not on created + updated. */
    pending: number
  }
  actions: HubspotTenantSyncAction[]
  skipped: HubspotTenantSyncAction[]
}

export const getOrganizationHubspotClient = () => {
  if (!client) {
    client = axios.create({
      baseURL: config.ORGANIZATION
    })

    configureClient(client, {
      baseURL: config.ORGANIZATION
    })
  }

  return client
}

export const organizationHubspotClient = {
  getHubspotCompanies: async (
    search?: string,
    limit: number = 100
  ): Promise<HubspotCompaniesResponse> => {
    const client = getOrganizationHubspotClient()
    const params: Record<string, string | number> = { limit }

    if (search) {
      params.search = search
    }

    const response = await client.get<HubspotCompaniesResponse>(
      '/v2/hubspot/companies',
      { params }
    )

    return response.data
  },

  getOrganizationHubspotData: async (
    orgId: string
  ): Promise<HubspotOrganizationData | null> => {
    const client = getOrganizationHubspotClient()

    try {
      const response = await client.get<HubspotOrganizationData>(
        `/v2/organizations/${orgId}/hubspot-data`
      )

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }

      throw error
    }
  },

  getHubspotCompanyById: async (
    companyId: string
  ): Promise<HubspotCompany | null> => {
    const client = getOrganizationHubspotClient()

    try {
      const response = await client.get<HubspotCompany>(
        `/v2/hubspot/companies/${companyId}`
      )

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null
      }

      throw error
    }
  },

  getOrganizationHubspotCoverage:
    async (): Promise<HubspotCoverageResponse> => {
      const client = getOrganizationHubspotClient()
      const response = await client.get<HubspotCoverageResponse>(
        '/v2/internal/organizations/hubspot-coverage'
      )

      return response.data
    },

  syncOrganizationHubspotTenants: async (params: {
    dryRun: boolean
    orgIds?: string[]
  }): Promise<HubspotTenantSyncResult> => {
    const client = getOrganizationHubspotClient()
    const response = await client.post<HubspotTenantSyncResult>(
      '/v2/internal/organizations/hubspot-tenant-sync',
      {
        dry_run: params.dryRun,
        ...(params.orgIds?.length ? { org_ids: params.orgIds } : {})
      }
    )

    return response.data
  }
}
